const toSafeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const hasOwn = (source, field) => (
  source != null && Object.prototype.hasOwnProperty.call(source, field)
);

const hasExplicitCanonicalField = (bankStatus, field, marker) => {
  if (hasOwn(bankStatus, marker)) {
    return bankStatus[marker] === true;
  }

  return hasOwn(bankStatus, field) || hasOwn(bankStatus?.onboardingImportStatus, field);
};

const ACTIVE_AI_PHASES = new Set(['pending', 'waiting_for_enrichment', 'running']);
const TERMINAL_AI_PHASES = new Set(['complete', 'unavailable', 'failed']);

const isActiveAiPhase = (phase) => ACTIVE_AI_PHASES.has(phase);
const isTerminalAiPhase = (phase) => TERMINAL_AI_PHASES.has(phase);

export const DASHBOARD_IMPORT_COMPLETION_DWELL_MS = 2800;
export const DASHBOARD_IMPORT_FRESH_COMPLETION_MS = 15000;
export const DASHBOARD_IMPORT_READY_TITLE = 'Sweet, your dashboard is ready';

export const INITIAL_IMPORT_STATUS_FIELDS = [
  'aiCategorizationEnabled',
  'onboardingImportState',
  'onboardingImportStartedAt',
  'currentImportTransactionCount',
  'connectionConfirmed',
  'connectedAccountsReady',
  'importQueued',
  'importRunning',
  'recentTransactionsImported',
  'recentHistoryReady',
  'datasetState',
  'datasetSettled',
  'datasetHasPersistedData',
  'reportingDataReady',
  'currentMonthKey',
  'currentMonthReady',
  'currentMonthTransactionCount',
  'importedMonthKeys',
  'olderMonthsReady',
  'dashboardDataReady',
  'dashboardReady',
  'reviewQueueReady',
  'canContinueOnboardingFromImportStep',
  'historicalImportStatus',
  'historicalImportInProgress',
  'importTickerEvents',
  'historicalImportComplete',
  'budgetAutoBuildReady',
  'transactionImportLocked',
  'aiRefinementLocked',
  'dataPreparationLocked',
  'reportsReady',
  'bankImportMutationLocked',
  'hasExplicitTransactionImportLocked',
  'hasExplicitAiRefinementLocked',
  'hasExplicitDataPreparationLocked',
  'hasExplicitReportsReady',
  'hasExplicitBankImportMutationLocked',
  'aiCategorizationPromptReady',
  'onboardingImportStatus',
  'initialImportBackgroundCategorizationStatus',
  'initialImportBackgroundCategorizationTotalCount',
  'initialImportBackgroundCategorizationProcessedCount',
  'initialImportBackgroundCategorizationAppliedCount',
  'initialImportBackgroundCategorizationCurrentMonthAppliedCount',
  'initialImportBackgroundCategorizationDiscardedCount',
  'initialImportBackgroundCategorizationFailedCount',
  'initialImportBackgroundCategorizationRunning',
  'initialImportBackgroundCategorizationCompletedAt',
  'initialImportBackgroundCategorizationResolvedCount',
  'initialImportBackgroundCategorizationRemainingCount',
  'initialImportBackgroundCategorizationHadUpdates',
  'initialImportBackgroundCategorizationRecentEvents',
  'importCycleKey',
  'estimatedSecondsRemaining',
  'estimatedTimeLabel',
  'importProgressRatio',
  'importProgressConfidence',
  'activeInstitutionName',
  'importInstitutionName',
  'connectedInstitutionName',
  'institutionName',
  'queuedImportCount',
  'queuedImportsCount',
  'queuedInstitutionCount',
  'queuedInstitutionsCount',
];

export const mergeInitialImportStatusFields = (baseStatus = {}, canonicalStatus = {}) => {
  const nextStatus = { ...(baseStatus || {}) };
  INITIAL_IMPORT_STATUS_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(canonicalStatus || {}, field)) {
      nextStatus[field] = canonicalStatus[field];
    }
  });
  return nextStatus;
};

// Client-side fail-closed projection for stale HTTP/push snapshots. The API is
// authoritative and already omits AI work when consent is off, but a cached
// pre-withdrawal snapshot must never keep the UI locked on, or visibly describe,
// an AI-only phase after the owner declines or revokes.
export const suppressAiImportState = (bankStatus = {}) => {
  if (bankStatus.onboardingImportStatus?.snapshotObservedAt) return bankStatus;
  const source = bankStatus || {};
  const canonical = source.onboardingImportStatus || {};
  const historicalImportComplete = Boolean(
    canonical.historicalImportComplete === true
      || source.historicalImportComplete === true
  );
  const transactionImportLocked = Boolean(
    canonical.transactionImportLocked === true
      || source.transactionImportLocked === true
      || (!historicalImportComplete && (
        canonical.phase === 'syncing_recent'
          || canonical.phase === 'syncing_historical'
          || canonical.phase === 'backfilling_history'
      ))
  );
  const readyWithoutAi = Boolean(
    canonical.canEnterDashboard === true
      && historicalImportComplete
      && !transactionImportLocked
  );

  return {
    ...source,
    aiCategorizationEnabled: false,
    initialImportBackgroundCategorizationStatus: 'idle',
    initialImportBackgroundCategorizationTotalCount: 0,
    initialImportBackgroundCategorizationProcessedCount: 0,
    initialImportBackgroundCategorizationAppliedCount: 0,
    initialImportBackgroundCategorizationCurrentMonthAppliedCount: 0,
    initialImportBackgroundCategorizationDiscardedCount: 0,
    initialImportBackgroundCategorizationFailedCount: 0,
    initialImportBackgroundCategorizationRunning: false,
    initialImportBackgroundCategorizationCompletedAt: null,
    initialImportBackgroundCategorizationResolvedCount: 0,
    initialImportBackgroundCategorizationRemainingCount: 0,
    initialImportBackgroundCategorizationHadUpdates: false,
    initialImportBackgroundCategorizationRecentEvents: [],
    transactionImportLocked,
    aiRefinementLocked: false,
    dataPreparationLocked: transactionImportLocked,
    reportsReady: readyWithoutAi,
    bankImportMutationLocked: transactionImportLocked,
    budgetAutoBuildReady: readyWithoutAi,
    hasExplicitTransactionImportLocked: true,
    hasExplicitAiRefinementLocked: true,
    hasExplicitDataPreparationLocked: true,
    hasExplicitReportsReady: true,
    hasExplicitBankImportMutationLocked: true,
    onboardingImportStatus: {
      ...canonical,
      aiPhase: 'idle',
      aiTotalCount: 0,
      aiProcessedCount: 0,
      aiRemainingCount: 0,
      aiAppliedCount: 0,
      aiFailedCount: 0,
      aiDiscardedCount: 0,
      reviewReady: readyWithoutAi,
      reviewQueueReady: readyWithoutAi,
      canUseBudgetAutobuild: readyWithoutAi,
      transactionImportLocked,
      aiRefinementLocked: false,
      dataPreparationLocked: transactionImportLocked,
      reportsReady: readyWithoutAi,
      bankImportMutationLocked: transactionImportLocked,
      phase: readyWithoutAi && canonical.phase === 'ai_categorizing'
        ? 'ready'
        : canonical.phase,
      dashboardPresentationState: readyWithoutAi
        && canonical.dashboardPresentationState === 'refining_categories'
        ? 'ready'
        : canonical.dashboardPresentationState,
    },
  };
};

export const getInitialImportBackgroundProgress = (bankStatus = {}) => {
  const totalCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationTotalCount);
  const processedCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationProcessedCount);
  const appliedCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationAppliedCount);
  const discardedCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationDiscardedCount);
  const failedCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationFailedCount);
  const remainingCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationRemainingCount);
  const resolvedCount = toSafeNumber(bankStatus?.initialImportBackgroundCategorizationResolvedCount);

  return {
    totalCount,
    processedCount,
    appliedCount,
    discardedCount,
    failedCount,
    remainingCount,
    resolvedCount,
    hasWork: totalCount > 0,
    progressLabel: totalCount > 0 ? `${Math.min(processedCount, totalCount)}/${totalCount} reviewed` : null,
  };
};

export const getHistoricalImportStatus = (bankStatus = {}) => {
  const startedAt = bankStatus?.onboardingImportStartedAt || null;
  const rawState = bankStatus?.onboardingImportState || 'not_started';
  const complete = Boolean(bankStatus?.historicalImportComplete);
  const failed = rawState === 'failed';
  const running = Boolean(startedAt) && !complete && !failed;

  return {
    startedAt,
    rawState,
    complete,
    failed,
    running,
    phase: !startedAt ? 'idle' : (failed ? 'failed' : (complete ? 'completed' : 'running')),
  };
};

export const getCanonicalBackgroundCategorizationStatus = (
  bankStatus = {},
  { aiCategorizationEnabled = bankStatus?.aiCategorizationEnabled !== false } = {}
) => {
  if (!aiCategorizationEnabled && !bankStatus.onboardingImportStatus?.snapshotObservedAt) {
    return {
      running: false,
      completed: false,
      remainingCount: 0,
      phase: 'idle',
    };
  }
  const canonicalImportStatus = bankStatus?.onboardingImportStatus || {};
  const canonicalAiPhase = typeof canonicalImportStatus.aiPhase === 'string'
    ? canonicalImportStatus.aiPhase
    : null;
  const canonicalAiRemainingCount = toSafeNumber(canonicalImportStatus.aiRemainingCount);
  const fallbackRunning = Boolean(
    bankStatus?.initialImportBackgroundCategorizationRunning
    && bankStatus?.onboardingImportStartedAt
  );
  const fallbackCompleted = Boolean(bankStatus?.initialImportBackgroundCategorizationCompletedAt);
  const hasCanonicalAiPhase = canonicalAiPhase === 'idle'
    || isActiveAiPhase(canonicalAiPhase)
    || isTerminalAiPhase(canonicalAiPhase);
  const running = hasCanonicalAiPhase
    ? isActiveAiPhase(canonicalAiPhase)
    : fallbackRunning;
  const completed = hasCanonicalAiPhase
    ? !running && (
      isTerminalAiPhase(canonicalAiPhase)
      || (
        canonicalImportStatus.phase === 'ready'
        && canonicalAiRemainingCount === 0
      )
    )
    : (!fallbackRunning && fallbackCompleted);

  return {
    running,
    completed,
    remainingCount: hasCanonicalAiPhase
      ? canonicalAiRemainingCount
      : toSafeNumber(bankStatus?.initialImportBackgroundCategorizationRemainingCount),
    phase: running ? 'running' : (completed ? 'completed' : 'idle'),
  };
};

export const getCanonicalReviewAvailability = (
  bankStatus = {},
  { aiCategorizationEnabled = bankStatus?.aiCategorizationEnabled !== false } = {}
) => {
  const canonicalImportStatus = bankStatus?.onboardingImportStatus || {};
  const hasExplicitReviewReady = typeof canonicalImportStatus.reviewReady === 'boolean';
  const canEnterDashboard = canonicalImportStatus.canEnterDashboard === true;
  const historicalImportComplete = Boolean(
    canonicalImportStatus.historicalImportComplete === true
      || bankStatus?.historicalImportComplete === true
      || canonicalImportStatus.phase === 'ready'
      || canonicalImportStatus.aiPhase === 'complete'
  );
  const recentTransactionsImported = Boolean(
    canonicalImportStatus.recentTransactionsImported === true
      || canonicalImportStatus.recentImportReady === true
      || bankStatus?.recentImportReady
      || canonicalImportStatus.recentHistoryReady === true
      || canonicalImportStatus.currentMonthReady === true
      || canonicalImportStatus.phase === 'syncing_historical'
      || canonicalImportStatus.phase === 'backfilling_history'
      || canonicalImportStatus.phase === 'ai_categorizing'
      || historicalImportComplete
      || toSafeNumber(canonicalImportStatus.currentMonthTransactionCount) > 0
      || toSafeNumber(canonicalImportStatus.recentTransactionCount) > 0
      || toSafeNumber(canonicalImportStatus.totalImportedTransactionCount) > 0
      || toSafeNumber(bankStatus?.currentImportTransactionCount) > 0
  );
  const aiPhase = typeof canonicalImportStatus.aiPhase === 'string'
    ? canonicalImportStatus.aiPhase
    : 'idle';
  const phase = typeof canonicalImportStatus.phase === 'string'
    ? canonicalImportStatus.phase
    : 'not_started';
  const hasExplicitPreparationLock = hasExplicitCanonicalField(
    bankStatus,
    'dataPreparationLocked',
    'hasExplicitDataPreparationLocked'
  );

  const reviewReady = canonicalImportStatus.canReviewExistingTransactions === true || (hasExplicitReviewReady
    ? canonicalImportStatus.reviewReady === true
    : (
      canEnterDashboard
      && historicalImportComplete
      && phase !== 'ai_categorizing'
      && !isActiveAiPhase(aiPhase)
    ));
  const locked = !reviewReady && (
    canEnterDashboard
    || (hasExplicitPreparationLock && canonicalImportStatus.dataPreparationLocked === true)
  );
  const lockReason = !locked
    ? null
    : hasExplicitPreparationLock && canonicalImportStatus.aiRefinementLocked === true
      ? 'ai_categorizing'
      : hasExplicitPreparationLock && canonicalImportStatus.transactionImportLocked === true
        ? 'historical_import'
    : !recentTransactionsImported
      ? 'recent_import'
      : (historicalImportComplete && aiCategorizationEnabled ? 'ai_categorizing' : 'historical_import');

  return {
    ready: reviewReady,
    locked,
    lockReason,
    canEnterDashboard,
    historicalImportComplete,
    recentTransactionsImported,
  };
};

export const getCanonicalBudgetAutobuildAvailability = (
  bankStatus = {},
  { aiCategorizationEnabled = bankStatus?.aiCategorizationEnabled !== false } = {}
) => {
  const canonicalImportStatus = bankStatus?.onboardingImportStatus || {};
  const hasExplicitBudgetUnlock = typeof canonicalImportStatus.canUseBudgetAutobuild === 'boolean';
  const historicalImportComplete = Boolean(
    Object.prototype.hasOwnProperty.call(canonicalImportStatus, 'historicalImportComplete')
      ? canonicalImportStatus.historicalImportComplete
      : bankStatus?.historicalImportComplete
  );
  const ready = hasExplicitBudgetUnlock
    ? canonicalImportStatus.canUseBudgetAutobuild
    : (
      Object.prototype.hasOwnProperty.call(bankStatus, 'budgetAutoBuildReady')
        ? Boolean(bankStatus?.budgetAutoBuildReady)
        : historicalImportComplete
    );
  const aiRefinementActive = aiCategorizationEnabled && (
    isActiveAiPhase(canonicalImportStatus.aiPhase)
    || canonicalImportStatus.phase === 'ai_categorizing'
    || canonicalImportStatus.aiRefinementLocked === true
  );

  return {
    ready,
    locked: !ready,
    lockReason: ready ? null : (
      historicalImportComplete && aiRefinementActive
        ? 'ai_refinement'
        : (historicalImportComplete ? 'awaiting_budget_snapshot' : 'historical_import')
    ),
    historicalImportComplete,
  };
};

export const getCanonicalPreparationStatus = (
  bankStatus = {},
  { aiCategorizationEnabled = bankStatus?.aiCategorizationEnabled !== false } = {}
) => {
  const canonicalImportStatus = bankStatus?.onboardingImportStatus || {};
  const historicalImportStatus = getHistoricalImportStatus(bankStatus);
  const reviewAvailability = getCanonicalReviewAvailability(bankStatus, { aiCategorizationEnabled });
  const canEnterDashboard = reviewAvailability.canEnterDashboard === true;
  const dashboardPresentationState = canonicalImportStatus.dashboardPresentationState || null;
  const categorizationStatus = getCanonicalBackgroundCategorizationStatus(
    bankStatus,
    { aiCategorizationEnabled }
  );
  const aiStillRunning = aiCategorizationEnabled && (
    categorizationStatus.running
    || isActiveAiPhase(canonicalImportStatus.aiPhase)
    || canonicalImportStatus.phase === 'ai_categorizing'
    || canonicalImportStatus.aiRefinementLocked === true
  );
  const recentHistoryReady = canonicalImportStatus.preparationScope === 'connections'
    ? canonicalImportStatus.recentHistoryReady === true : canonicalImportStatus.recentHistoryReady === true
    || bankStatus?.recentHistoryReady === true
    || canonicalImportStatus.recentTransactionsImported === true
    || bankStatus?.recentTransactionsImported === true
    || historicalImportStatus.complete === true;

  if (!historicalImportStatus.startedAt || historicalImportStatus.failed || canonicalImportStatus.failed === true) {
    return {
      phase: 'idle',
      active: false,
      success: false,
      historicalImportComplete: historicalImportStatus.complete,
      reviewReady: reviewAvailability.ready,
    };
  }

  if (!canEnterDashboard || !recentHistoryReady || dashboardPresentationState === 'syncing_recent') {
    return {
      phase: 'recent_import',
      active: true,
      success: false,
      historicalImportComplete: false,
      reviewReady: false,
    };
  }

  if (!historicalImportStatus.complete || dashboardPresentationState === 'backfilling_history') {
    return {
      phase: 'historical_import',
      active: true,
      success: false,
      historicalImportComplete: false,
      reviewReady: reviewAvailability.ready,
    };
  }

  if (aiStillRunning || dashboardPresentationState === 'refining_categories') {
    return {
      phase: 'ai_review',
      active: true,
      success: false,
      historicalImportComplete: true,
      reviewReady: reviewAvailability.ready,
    };
  }

  return {
    phase: 'success',
    active: false,
    success: true,
    historicalImportComplete: true,
    reviewReady: reviewAvailability.ready,
  };
};

const getCanonicalImportStatus = (bankStatus = {}) => bankStatus?.onboardingImportStatus || {};

const normalizeExperienceContext = (context) => (
  typeof context === 'string' ? context.trim().toLowerCase() : 'none'
);

const INITIAL_DASHBOARD_PREPARATION_CONTEXTS = new Set([
  'none',
  'onboarding',
  'initial_onboarding',
]);

const POST_ONBOARDING_IMPORT_CONTEXTS = new Set([
  'new_bank_connection',
  'net_worth_connection',
  'net_worth_import',
  'net_worth_account_connection',
  'net_worth_account_import',
  'net_worth',
]);

const NET_WORTH_IMPORT_CONTEXTS = new Set([
  'net_worth_connection',
  'net_worth_import',
  'net_worth_account_connection',
  'net_worth_account_import',
  'net_worth',
]);

const firstNonBlankString = (...values) => {
  const found = values.find((value) => typeof value === 'string' && value.trim());
  return found ? found.trim() : null;
};

const getActiveImportInstitutionName = (bankStatus = {}) => {
  const canonicalImportStatus = getCanonicalImportStatus(bankStatus);
  return firstNonBlankString(
    canonicalImportStatus.activeInstitutionName,
    canonicalImportStatus.importInstitutionName,
    canonicalImportStatus.connectedInstitutionName,
    canonicalImportStatus.institutionName,
    bankStatus.activeInstitutionName,
    bankStatus.importInstitutionName,
    bankStatus.connectedInstitutionName,
    bankStatus.institutionName
  );
};

const getQueuedImportCount = (bankStatus = {}) => {
  const canonicalImportStatus = getCanonicalImportStatus(bankStatus);
  const count = toSafeNumber(
    canonicalImportStatus.queuedImportCount
      ?? canonicalImportStatus.queuedImportsCount
      ?? canonicalImportStatus.queuedInstitutionCount
      ?? canonicalImportStatus.queuedInstitutionsCount
      ?? bankStatus.queuedImportCount
      ?? bankStatus.queuedImportsCount
      ?? bankStatus.queuedInstitutionCount
      ?? bankStatus.queuedInstitutionsCount
  );
  return Math.max(0, count);
};

const getImportPreparationTitle = ({ isInitialDashboardPreparationContext }) => {
  if (isInitialDashboardPreparationContext) return 'Preparing Your Dashboard';
  return 'Importing Transactions';
};

export const isFreshDashboardImportCompletion = (
  dashboardImportExperience = {},
  nowMs = Date.now()
) => {
  if (dashboardImportExperience?.success !== true) return false;

  const rawTimestamp = dashboardImportExperience.lastUpdatedAt
    || dashboardImportExperience.completedAt
    || null;
  if (!rawTimestamp) return false;

  const completedAtMs = Date.parse(rawTimestamp);
  if (!Number.isFinite(completedAtMs)) return false;

  const ageMs = nowMs - completedAtMs;
  return ageMs >= 0 && ageMs <= DASHBOARD_IMPORT_FRESH_COMPLETION_MS;
};

export const getCanonicalImportExperienceContext = (bankStatus = {}) => {
  const canonicalImportStatus = getCanonicalImportStatus(bankStatus);
  const context = canonicalImportStatus.experienceContext
    || bankStatus?.importExperienceContext
    || bankStatus?.onboardingImportExperienceContext
    || 'none';
  return normalizeExperienceContext(context);
};

export const isNetWorthImportExperienceContext = (bankStatus = {}) => (
  NET_WORTH_IMPORT_CONTEXTS.has(getCanonicalImportExperienceContext(bankStatus))
);

export const getReviewQueueCandidateCount = (bankStatus = {}) => {
  const canonicalImportStatus = getCanonicalImportStatus(bankStatus);
  const count = toSafeNumber(
    canonicalImportStatus.reviewQueueCandidateCount
      ?? bankStatus?.reviewQueueCandidateCount
  );
  return count > 0 ? count : null;
};

const getArrayField = (...values) => {
  const found = values.find((value) => Array.isArray(value));
  return found ? found.filter(Boolean).map((value) => String(value)) : [];
};

const hasStartedCanonicalImport = (bankStatus = {}) => {
  const canonicalImportStatus = getCanonicalImportStatus(bankStatus);
  const phase = canonicalImportStatus.phase || 'not_started';
  return Boolean(
    bankStatus?.onboardingImportStartedAt
      || canonicalImportStatus.importQueued
      || canonicalImportStatus.importRunning
      || (phase && phase !== 'not_started')
  );
};

export const getDashboardImportExperience = (
  bankStatus = {},
  selectedMonth = null,
  { aiCategorizationEnabled = bankStatus?.aiCategorizationEnabled !== false } = {}
) => {
  const canonicalImportStatus = getCanonicalImportStatus(bankStatus);
  const experienceContext = getCanonicalImportExperienceContext(bankStatus);
  const isInitialDashboardPreparationContext = INITIAL_DASHBOARD_PREPARATION_CONTEXTS.has(experienceContext);
  const isPostOnboardingImportContext = POST_ONBOARDING_IMPORT_CONTEXTS.has(experienceContext);
  const isNetWorthImportContext = NET_WORTH_IMPORT_CONTEXTS.has(experienceContext);
  const activeInstitutionName = getActiveImportInstitutionName(bankStatus);
  const queuedImportCount = getQueuedImportCount(bankStatus);
  const preparationStatus = getCanonicalPreparationStatus(bankStatus, { aiCategorizationEnabled });
  const reviewAvailability = getCanonicalReviewAvailability(bankStatus, { aiCategorizationEnabled });
  const backgroundStatus = getCanonicalBackgroundCategorizationStatus(
    bankStatus,
    { aiCategorizationEnabled }
  );
  const aiWorking = backgroundStatus.running;
  const aiWaiting = canonicalImportStatus.aiPhase === 'waiting_for_enrichment';
  const aiUnavailable = ['unavailable', 'failed'].includes(canonicalImportStatus.aiPhase);
  const importedMonthKeys = getArrayField(
    canonicalImportStatus.importedMonthKeys,
    bankStatus.importedMonthKeys
  );
  const currentMonthKey = canonicalImportStatus.currentMonthKey || bankStatus.currentMonthKey || null;
  const selectedMonthKey = selectedMonth || currentMonthKey;
  const historicalImportComplete = Boolean(
    canonicalImportStatus.historicalImportComplete
      ?? bankStatus.historicalImportComplete
  );
  const recentHistoryReady = canonicalImportStatus.preparationScope === 'connections'
    ? canonicalImportStatus.recentHistoryReady === true : Boolean(
    canonicalImportStatus.recentHistoryReady
      || bankStatus.recentHistoryReady
      || canonicalImportStatus.recentTransactionsImported
      || bankStatus.recentTransactionsImported
      || historicalImportComplete
  );
  const reportingDataReady = canonicalImportStatus.reportingDataReady === true
    || (
      !Object.prototype.hasOwnProperty.call(canonicalImportStatus, 'reportingDataReady')
      && canonicalImportStatus.dashboardDataReady === true
      && canonicalImportStatus.datasetSettled !== false
    );
  const selectedMonthReady = selectedMonthKey && selectedMonthKey === currentMonthKey
    ? canonicalImportStatus.currentMonthReady === true || reportingDataReady
    : reportingDataReady;
  const canEnterDashboard = canonicalImportStatus.canEnterDashboard === true;
  const started = hasStartedCanonicalImport(bankStatus);
  const failed = canonicalImportStatus.failed === true;
  // Opening the settled queue does not complete the new connection's workflow.
  const reviewReady = canonicalImportStatus.canReviewExistingTransactions === true
    ? canonicalImportStatus.reviewReady === true : reviewAvailability.ready === true;
  const awaitingAiDecision = bankStatus.aiCategorizationPromptReady === true && !aiCategorizationEnabled;
  const showDashboardPreparation = Boolean(
    (isInitialDashboardPreparationContext || isPostOnboardingImportContext)
      && canonicalImportStatus.preparationDismissed !== true
      && started
      && canEnterDashboard
      && (!failed || isPostOnboardingImportContext)
  );
  const active = Boolean(showDashboardPreparation && !reviewReady && !failed);
  const visible = Boolean(showDashboardPreparation && (!reviewReady || awaitingAiDecision));
  const refinementUnavailable = aiUnavailable && historicalImportComplete && reviewReady && aiCategorizationEnabled;
  const complete = reviewReady && !awaitingAiDecision && !refinementUnavailable;
  const phase = complete
    ? 'ready'
    : (!recentHistoryReady
        ? 'importing_recent'
        : (!historicalImportComplete
            ? 'importing_older_months'
            : 'refining_categories'));
  const selectedMonthImporting = Boolean(active && selectedMonthKey && !selectedMonthReady
    && canonicalImportStatus.canReviewExistingTransactions !== true);
  const transactionCount = toSafeNumber(
    canonicalImportStatus.currentImportTransactionCount
      ?? bankStatus.currentImportTransactionCount
  );
  const aiRemainingCount = toSafeNumber(canonicalImportStatus.aiRemainingCount);
  const backendEstimatedSeconds = toSafeNumber(
    canonicalImportStatus.estimatedSecondsRemaining
      ?? bankStatus.estimatedSecondsRemaining
  );
  const backendProgressRatio = Math.max(0, Math.min(1, Number(
    canonicalImportStatus.progressRatio
      ?? bankStatus.importProgressRatio
      ?? 0
  ) || 0));
  const backendTimeLabel = canonicalImportStatus.estimatedTimeLabel
    || bankStatus.estimatedTimeLabel
    || null;
  const recentEvents = Array.isArray(bankStatus?.initialImportBackgroundCategorizationRecentEvents)
    ? bankStatus.initialImportBackgroundCategorizationRecentEvents
    : [];
  const lastUpdatedAt = canonicalImportStatus.lastUpdatedAt
    || canonicalImportStatus.completedAt
    || bankStatus.onboardingImportCompletedAt
    || bankStatus.initialImportBackgroundCategorizationCompletedAt
    || null;
  const steps = [
    {
      key: 'connecting_accounts',
      label: 'Connecting Accounts',
      status: 'complete',
      statusLabel: 'Complete',
      progress: 1,
    },
    {
      key: 'recent_history',
      label: 'Importing recent history',
      status: recentHistoryReady ? 'complete' : 'active',
      statusLabel: recentHistoryReady ? 'Complete' : 'In progress',
      progress: recentHistoryReady ? 1 : 0.42,
    },
    {
      key: 'older_months',
      label: 'Importing older months',
      status: historicalImportComplete ? 'complete' : (recentHistoryReady ? 'active' : 'pending'),
      statusLabel: historicalImportComplete ? 'Complete' : (recentHistoryReady ? 'In progress' : 'Queued'),
      progress: historicalImportComplete ? 1 : (recentHistoryReady ? 0.38 : 0),
    },
    {
      key: 'refining_categories',
      label: 'Refining categories',
      status: !historicalImportComplete ? 'pending' : aiUnavailable ? 'unavailable' : aiWaiting ? 'pending' : aiWorking ? 'active' : !aiCategorizationEnabled
        ? 'pending'
        : (backgroundStatus.running
            ? 'active'
            : (backgroundStatus.completed ? 'complete' : 'pending')),
      statusLabel: !historicalImportComplete ? (aiCategorizationEnabled ? 'Queued' : 'Pending') : aiUnavailable ? 'Unavailable' : aiWaiting ? 'Waiting for bank data' : aiWorking ? 'In progress' : !aiCategorizationEnabled
        ? 'Pending'
        : (backgroundStatus.running
            ? 'In progress'
            : (backgroundStatus.completed ? 'Complete' : 'Queued')),
      progress: backgroundStatus.completed
        ? 1
        : (backgroundStatus.running && aiRemainingCount > 0
            ? Math.max(0.12, Math.min(0.92, 1 - (aiRemainingCount / Math.max(aiRemainingCount + toSafeNumber(bankStatus.initialImportBackgroundCategorizationResolvedCount), 1))))
            : (backgroundStatus.running ? 0.34 : 0)),
    },
  ];
  const activeStepIndex = steps.findIndex((step) => step.status === 'active');
  const lastCompleteStepIndex = steps.map((step) => step.status).lastIndexOf('complete');
  const currentStepIndex = reviewReady
    ? steps.length
    : Math.max(0, activeStepIndex >= 0 ? activeStepIndex : lastCompleteStepIndex) + 1;

  return {
    visible,
    active,
    failed,
    waiting: canonicalImportStatus.retrying === true,
    refinementUnavailable,
    success: complete,
    completionDwellEligible: isInitialDashboardPreparationContext
      && canonicalImportStatus.preparationDismissed !== true,
    phase,
    title: complete
      ? DASHBOARD_IMPORT_READY_TITLE
      : isPostOnboardingImportContext
        ? (activeInstitutionName ? `Preparing ${activeInstitutionName}` : 'Preparing accounts')
        : getImportPreparationTitle({ isInitialDashboardPreparationContext }),
    subtitle: null,
    activeInstitutionName,
    queuedImportCount,
    queueLabel: isNetWorthImportContext && queuedImportCount > 0 ? `${queuedImportCount} more queued` : null,
    isInitialDashboardPreparationContext,
    isPostOnboardingImportContext,
    isNetWorthImportContext,
    lastUpdatedAt,
    completedAt: complete ? lastUpdatedAt : null,
    selectedMonthReady,
    selectedMonthImporting,
    currentMonthKey,
    selectedMonthKey,
    importedMonthKeys,
    transactionCount,
    estimatedSecondsRemaining: canonicalImportStatus.retrying === true ? null : backendEstimatedSeconds,
    tickerEvents: isPostOnboardingImportContext ? [] : recentEvents,
    steps: canonicalImportStatus.retrying === true ? steps.map((step) => step.status === 'active'
      ? { ...step, status: 'pending', statusLabel: 'Waiting' } : step) : steps,
    currentStepIndex,
    totalStepCount: steps.length,
    timeLabel: canonicalImportStatus.retrying === true ? 'Waiting for bank' : isPostOnboardingImportContext ? null : complete
      ? null
      : (aiWaiting && historicalImportComplete ? 'Waiting for merchant details from your bank'
        : awaitingAiDecision && historicalImportComplete ? 'Choose whether to refine categories with AI'
        : backendTimeLabel
          || (phase === 'importing_recent'
              ? 'Connecting and importing recent transactions'
              : (phase === 'importing_older_months'
                  ? 'Importing older transactions securely'
                  : 'Refining categories in the background'))),
    timeProgress: complete
      ? 1
      : (awaitingAiDecision && historicalImportComplete ? 0.75 : backendProgressRatio > 0
          ? backendProgressRatio
          : (phase === 'importing_recent'
              ? 0.26
              : (phase === 'importing_older_months'
                  ? 0.58
                  : 0.82))),
    progressConfidence: canonicalImportStatus.progressConfidence
      || bankStatus.importProgressConfidence
      || 'state_only',
    cycleKey: canonicalImportStatus.importCycleKey
      || bankStatus.importCycleKey
      || bankStatus.onboardingImportStartedAt
      || null,
    preparationStatus,
  };
};

export const deriveInitialImportExperience = (
  bankStatus = {},
  { aiCategorizationEnabled = bankStatus?.aiCategorizationEnabled !== false } = {}
) => {
  const historicalImportStatus = getHistoricalImportStatus(bankStatus);
  const onboardingImportStartedAt = historicalImportStatus.startedAt;
  const historicalImportState = historicalImportStatus.rawState;
  const historicalImportRunning = historicalImportStatus.running;
  const historicalImportComplete = historicalImportStatus.complete;
  const canonicalBackgroundStatus = getCanonicalBackgroundCategorizationStatus(
    bankStatus,
    { aiCategorizationEnabled }
  );
  const backgroundCategorizationRunning = canonicalBackgroundStatus.running;
  const backgroundCategorizationCompleted = canonicalBackgroundStatus.completed;
  const backgroundProgress = getInitialImportBackgroundProgress(bankStatus);
  const backgroundCategorizationStatus = {
    running: backgroundCategorizationRunning,
    completed: backgroundCategorizationCompleted,
    phase: canonicalBackgroundStatus.phase,
    ...backgroundProgress,
  };
  const initialImportExperienceActive = historicalImportRunning || backgroundCategorizationRunning;
  const recentEvents = Array.isArray(bankStatus?.initialImportBackgroundCategorizationRecentEvents)
    ? bankStatus.initialImportBackgroundCategorizationRecentEvents
    : [];
  const reviewAvailability = getCanonicalReviewAvailability(bankStatus, { aiCategorizationEnabled });
  const preparationStatus = getCanonicalPreparationStatus(bankStatus, { aiCategorizationEnabled });
  const dashboardPreparation = getDashboardImportExperience(
    bankStatus,
    null,
    { aiCategorizationEnabled }
  );

  return {
    onboardingImportStartedAt,
    historicalImportState,
    historicalImportRunning,
    historicalImportComplete,
    historicalImportStatus,
    backgroundCategorizationRunning,
    backgroundCategorizationCompleted,
    backgroundProgress,
    backgroundCategorizationStatus,
    initialImportExperienceActive,
    recentEvents,
    reviewAvailability,
    preparationStatus,
    dashboardPreparation,
  };
};

export const isBankImportMutationLocked = (bankStatus = {}) => {
  const canonicalImportStatus = bankStatus?.onboardingImportStatus || {};
  const hasExplicitCanonicalLock = hasExplicitCanonicalField(
    bankStatus,
    'dataPreparationLocked',
    'hasExplicitDataPreparationLocked'
  );

  if (hasExplicitCanonicalLock) {
    return bankStatus?.dataPreparationLocked === true
      || canonicalImportStatus?.dataPreparationLocked === true;
  }

  if (hasExplicitCanonicalField(
    bankStatus,
    'bankImportMutationLocked',
    'hasExplicitBankImportMutationLocked'
  )) {
    return bankStatus?.bankImportMutationLocked === true
      || canonicalImportStatus?.bankImportMutationLocked === true;
  }

  return deriveInitialImportExperience(bankStatus)?.preparationStatus?.active === true;
};

export const isNewBankConnectionMutationLocked = isBankImportMutationLocked;

export const isNetWorthImportMutationLocked = isBankImportMutationLocked;

export const isTransactionPreparationLocked = (bankStatus = {}, transaction = {}) => {
  if (getCanonicalReviewAvailability(bankStatus).locked) return true;
  const status = bankStatus?.onboardingImportStatus || {};
  if (status.dataPreparationLocked !== true || status.canReviewExistingTransactions !== true) return false;
  if (['pending', 'waiting_for_queue', 'processing'].includes(transaction.aiProcessingStatus || transaction.ai_processing_status)) return true;
  const accountId = transaction.accountId || transaction.account_id;
  const connectionId = transaction.providerConnectionId || transaction.provider_connection_id || transaction.plaid_item_id;
  return (status.preparingConnections || []).some((connection) => (
    connection.accountIds?.includes(accountId)
    || (connection.connectionId === connectionId && connection.provider === String(transaction.source || '').toLowerCase())
  ));
};
